import type { ControlSettingStateType } from '~/presentation/reports/common/type';

import {
  REPORT_FILTER_STICKY_ID,
  REPORT_FILTER_STICKY_TOP_POLARIS,
  REPORT_FILTER_WIDTH,
} from '../constants';
import { EControlSettingType, ERFilterKey } from '~/presentation/reports/common/type';
import { ControlSettings } from '~/presentation/reports/common';
import { useDateTimePickerContext } from '~/presentation/ui';
import { BlockStack, Box, Card } from '@shopify/polaris';
import { FaqDataNumbersTrigger } from '~/modules/faq';
import { useReportControlStore } from '../stores';
import { useStickyElementHeight } from '~/hooks';
import { usePreset } from '../hooks';
import { useRef } from 'react';

interface ReportFilterPanelProps {
  state?: ControlSettingStateType;
}

export const ReportFilterPanel = ({ state }: ReportFilterPanelProps) => {
  const onControlChange = useReportControlStore((state) => state.onControlChange);
  const reportControlData = useReportControlStore((state) => state.reportControlData);
  const openReportControl = useReportControlStore((state) => state.openReportControl);
  const setReportControlData = useReportControlStore((state) => state.setReportControlData);

  const { dateTimePicked, resetDateVersion } = useDateTimePickerContext();

  const { preset } = usePreset({
    setControlData: setReportControlData,
  });

  const elementRef = useRef<HTMLDivElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const maxHeight = useStickyElementHeight({
    elementRef,
    parentRef,
    offset: 80,
  });

  if (!preset) {
    return null;
  }

  const width = !openReportControl ? 0 : REPORT_FILTER_WIDTH;
  const opacity = openReportControl ? 1 : 0;

  return (
    <div
      ref={parentRef}
      style={{
        width,
        position: 'relative',
        opacity,
      }}
    >
      <Box
        position="sticky"
        insetBlockStart={REPORT_FILTER_STICKY_TOP_POLARIS}
        id={REPORT_FILTER_STICKY_ID}
      >
        <BlockStack gap="400">
          <Card padding="0">
            <div ref={elementRef}>
              <ControlSettings
                data={reportControlData || preset.presetData}
                defaultData={preset.presetData}
                presetUI={preset.presetUI}
                state={state}
                type={EControlSettingType.REPORT}
                controlChange={onControlChange}
                maxHeight={maxHeight}
                onDeleteControlUI={onDeleteControlUI}
              />
            </div>
          </Card>

          <FaqDataNumbersTrigger />
        </BlockStack>
      </Box>
    </div>
  );

  function onDeleteControlUI(id: string) {
    switch (id) {
      case ERFilterKey.EXPERIMENT:
        const isFilteredByVersion = dateTimePicked.isVersion;
        isFilteredByVersion && resetDateVersion();
        break;
      default:
        break;
    }
  }
};
